import { useState, useRef, useEffect } from 'react';

// ── Standalone Kids Conference DEMO ────────────────────────────────────────
// A fully self-contained sandbox at /demo — fake data, no login, no backend.
// Mal (admin) and the Kids Team Lead render inside the REAL app chrome (teal
// sidebar + topbar + the same stat-card / panel / data-table components the
// live admin app uses), reskinned teal with Kids content. The two SL personas
// render the servant-leader schedule page. Everything is interactive and resets
// on reload or "Switch persona". Decoupled from the real app on purpose.

const TEAL = '#6AA292';
const ACCENT = '#FFDB7C';

// Shared group-color system. Group names carry a color used for their pills in
// Daily Details, the Rotations group editor, and the printed sheet.
const GROUP_COLORS = { Green: '#3FA34D', Coral: '#FF7F50', Yellow: '#FFD23F', Teal: '#6AA292', Blue: '#3B82C4', Pink: '#EC6FAA', Red: '#E0524A', Orange: '#FF9B71', Purple: '#8E6FBF' };
const COLOR_PRESETS = ['#6AA292', '#3FA34D', '#3B82C4', '#8E6FBF', '#EC6FAA', '#FF7F50', '#FFD23F', '#E0524A', '#FF9B71', '#202020'];
const colorFor = name => GROUP_COLORS[name] || TEAL;
function textOn(hex) {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return '#fff';
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? '#202020' : '#fff';
}

const fmt = t => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

// ── Fake data ───────────────────────────────────────────────────────────────
const PROGRAMS = ['Elementary', '5-6', '3-4'];
// Pass level defaults to "Kids Pass" for everyone in Kids, unless a higher pass
// from another team wins (single-record, badge-highest rule) — e.g. Sam also
// serves Production and carries an AA Pass.
const KIDS_PEOPLE = [
  { name: 'Jordan Lee', email: 'jordan.lee@email.com', program: 'Elementary', role: 'Small Group Leader', maxSessions: 2, free: ['Fri AM', 'Fri PM', 'Sat AM'], status: 'accepted', statusLabel: 'Accepted · 2 blocks', registered: true, passLevel: 'Kids Pass', radio: true },
  { name: 'Casey Kim', email: 'casey.kim@email.com', program: 'Elementary', role: 'Activity Leader', maxSessions: 1, free: ['Sat PM'], status: 'pending', statusLabel: 'Pending · 1 block', registered: true, passLevel: 'Kids Pass', radio: false },
  { name: 'Riley Fox', email: 'riley.fox@email.com', program: 'Elementary', role: 'Check-In', maxSessions: 3, free: ['Fri AM', 'Fri PM', 'Sat AM', 'Sat PM'], status: 'accepted', statusLabel: 'Accepted · 3 blocks', registered: false, passLevel: 'Kids Pass', radio: false },
  { name: 'Sam Rivera', email: 'sam.rivera@email.com', program: '5-6', role: 'Small Group Leader', maxSessions: 3, free: ['Fri AM', 'Fri PM', 'Sat AM', 'Sat PM'], status: 'accepted', statusLabel: 'Accepted · 3 blocks', registered: true, passLevel: 'AA Pass', radio: true },
  { name: 'Drew Park', email: 'drew.park@email.com', program: '5-6', role: 'Party Team', maxSessions: 2, free: ['Fri PM', 'Sat PM'], status: 'pending', statusLabel: 'Pending · 1 block', registered: true, passLevel: 'Kids Pass', radio: false },
  { name: 'Avery Cole', email: 'avery.cole@email.com', program: '3-4', role: 'Snacks', maxSessions: 2, free: ['Fri PM', 'Sat PM'], status: 'accepted', statusLabel: 'Accepted · 1 block', registered: true, passLevel: 'Kids Pass', radio: false },
  { name: 'Quinn Diaz', email: 'quinn.diaz@email.com', program: '3-4', role: 'Small Group Leader', maxSessions: 1, free: ['Sat AM'], status: 'unplaced', statusLabel: 'Not yet placed', registered: false, passLevel: 'Kids Pass', radio: false },
];
const KIDS_BLOCKS = {
  Elementary: [
    { id: 'eb1', title: 'Friday — Session 1', meta: 'Friday · 9:00 – 11:30 AM', positions: [
      { id: 'p1', label: 'Small Group Leader — Red', person: 'Jordan Lee', status: 'accepted' },
      { id: 'p2', label: 'Activity Leader', person: 'Casey Kim', status: 'pending' },
      { id: 'p3', label: 'Check-In', open: true },
    ] },
    { id: 'eb2', title: 'Friday — Session 2', meta: 'Friday · 4:00 – 6:30 PM', positions: [
      { id: 'p4', label: 'Small Group Leader — Red', person: 'Jordan Lee', status: 'accepted' },
      { id: 'p5', label: 'Snacks', open: true },
    ] },
  ],
  '5-6': [
    { id: 'fb1', title: 'Friday — Session 1', meta: 'Friday · 9:00 – 11:30 AM', positions: [
      { id: 'p6', label: 'Small Group Leader — Blue', person: 'Sam Rivera', status: 'accepted' },
      { id: 'p7', label: 'Party Team', person: 'Drew Park', status: 'pending' },
    ] },
  ],
  '3-4': [
    { id: 'tb1', title: 'Saturday — Session 1', meta: 'Saturday · 9:00 – 11:30 AM', positions: [
      { id: 'p8', label: 'Snacks', person: 'Avery Cole', status: 'accepted' },
      { id: 'p9', label: 'Small Group Leader — Green', open: true },
    ] },
  ],
};
const MEAL_TOTALS = [
  { day: 'Friday', meal: 'Dinner', count: 14 },
  { day: 'Saturday', meal: 'Lunch', count: 9 },
  { day: 'Saturday', meal: 'Dinner', count: 12 },
];
// Full read-only rosters for the main teams that staff Kids programs, grouped
// by team → block → position. Mal sees who each main lead has rostered (or not).
const CONTRIB_TEAMS = [
  { team: 'Production', blocks: [
    { title: 'Session 1 AV', day: 'Friday', start: '09:00', time: '9:00 AM', positions: [
      { label: 'Elementary Audio A1', person: 'Chris Ford' }, { label: 'Elementary Video V1', person: 'Dana Webb' }, { label: 'Elementary Lighting', open: true } ] },
    { title: 'Session 2 AV', day: 'Friday', start: '16:00', time: '4:00 PM', positions: [
      { label: 'Elementary Audio A1', person: 'Chris Ford' }, { label: 'Elementary Video V1', open: true } ] },
    { title: 'Kids Room AV', day: 'Saturday', start: '09:00', time: '9:00 AM', positions: [
      { label: '5-6 Audio A2', person: 'Miguel Santos' }, { label: '5-6 Playback', person: 'Dana Webb' } ] },
  ] },
  { team: 'Worship', blocks: [
    { title: 'Kids Worship Set', day: 'Saturday', start: '09:00', time: '9:00 AM', positions: [
      { label: '5-6 Worship Lead', person: 'Pat Quinn' }, { label: '5-6 Acoustic', open: true } ] },
    { title: 'Littles Songs', day: 'Friday', start: '16:00', time: '4:00 PM', positions: [
      { label: 'Littles Worship Lead', person: 'Bri Adams' } ] },
  ] },
  { team: 'Comms', blocks: [
    { title: 'Photo Coverage', day: 'Friday', start: '09:00', time: '9:00 AM', positions: [
      { label: 'Elementary Photographer', open: true } ] },
  ] },
];
const ROT_INIT = {
  sessions: ['Friday 1', 'Friday 2', 'Saturday 1'],
  activities: ['Games', 'Crafts', 'Snack'],
  groups: ['Red', 'Blue', 'Green'],
  groupColors: { Red: '#E0524A', Blue: '#3B82C4', Green: '#3FA34D' },
  grid: {
    'Friday 1': { Games: 'Red', Crafts: 'Blue', Snack: 'Green' },
    'Friday 2': { Games: 'Green', Crafts: 'Red', Snack: 'Blue' },
    'Saturday 1': { Games: 'Blue', Crafts: 'Green', Snack: 'Red' },
  },
};
const KIDS_ONLY_SCHEDULE = [
  { day: 'Friday', title: 'Elementary — Session 1', start: '09:00', end: '11:30', pos: 'Small Group Leader — Red', dept: 'Kids' },
  { day: 'Friday', title: 'Elementary — Session 2', start: '16:00', end: '18:30', pos: 'Small Group Leader — Red', dept: 'Kids' },
  { day: 'Friday', title: 'Dinner', start: '18:30', meal: true, dept: 'Kids' },
  { day: 'Saturday', title: 'Elementary — Session 1', start: '09:00', end: '11:30', pos: 'Small Group Leader — Red', dept: 'Kids' },
];
const BOTH_SCHEDULE = [
  { day: 'Friday', title: 'Production Setup', start: '07:00', end: '09:00', pos: 'Stage Crew', dept: 'Main' },
  { day: 'Friday', title: 'Lunch', start: '12:30', meal: true, dept: 'Main' },
  { day: 'Friday', title: 'Elementary — Session 2', start: '16:00', end: '18:30', pos: 'Activity Leader', dept: 'Kids' },
  { day: 'Friday', title: 'Dinner', start: '18:30', meal: true, dept: 'Kids' },
  { day: 'Saturday', title: 'Production Teardown', start: '21:00', end: '23:00', pos: 'Stage Crew', dept: 'Main' },
];
const DEMO_SESSIONS = [
  { id: 's1', title: 'Elementary — Session 1', day: 'Friday', start: '09:00', period: 'morning' },
  { id: 's2', title: 'Elementary — Session 2', day: 'Friday', start: '16:00', period: 'evening' },
  { id: 's3', title: 'Elementary — Session 1', day: 'Saturday', start: '09:00', period: 'morning' },
  { id: 's4', title: 'Elementary — Session 2', day: 'Saturday', start: '16:00', period: 'evening' },
];

const PERSONAS = [
  { id: 'sl-kids', label: 'SL — Kids Only', kind: 'sl', desc: 'Servant leader serving only in Kids. Teal schedule, Kids meal logic, Most-helpful-3 form.' },
  { id: 'sl-both', label: 'SL — Kids + Main', kind: 'sl', desc: 'A both-server. Merged schedule across departments, union meals, "also serving" line.' },
  { id: 'lead', label: 'Kids Team Lead', kind: 'shell', desc: 'Three program rosters with Most-helpful-3 availability — in the real team-lead layout.' },
  { id: 'mal', label: 'Kids Admin (Mal)', kind: 'shell', desc: 'Admin layout: overview, people, rotations, daily details, meal totals, detail sheets, team lead access, SL manual, settings.' },
  { id: 'mal-combined', label: 'Kids Admin + Team Lead (Mal)', kind: 'shell', desc: 'One combined view (to show Mal the option): the admin tabs plus the team-lead tabs — rosters, schedule, docs, contributing — with settings by her name.' },
];

const ICONS = {
  overview: <svg className="nav-icon" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/></svg>,
  people: <svg className="nav-icon" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M2 14c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  roster: <svg className="nav-icon" viewBox="0 0 16 16" fill="none"><rect x="1" y="3" width="14" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M4 7h4M4 10h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="12" cy="7" r="1" fill="currentColor"/></svg>,
  schedule: <svg className="nav-icon" viewBox="0 0 16 16" fill="none"><rect x="1" y="2" width="14" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M5 1v2M11 1v2M1 6h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  meals: <svg className="nav-icon" viewBox="0 0 16 16" fill="none"><path d="M8 2v5M5 4c0 2.761 1.343 5 3 5s3-2.239 3-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M8 9v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  rotations: <svg className="nav-icon" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/><path d="M8 2v6l4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  teams: <svg className="nav-icon" viewBox="0 0 16 16" fill="none"><circle cx="5" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5"/><circle cx="11" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M1 14c0-2.21 1.79-4 4-4M15 14c0-2.21-1.79-4-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  manual: <svg className="nav-icon" viewBox="0 0 16 16" fill="none"><rect x="3" y="1" width="10" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M6 5h4M6 8h4M6 11h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  access: <svg className="nav-icon" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  settings: <svg className="nav-icon" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M2.929 2.929l1.06 1.06M12.01 12.01l1.06 1.06M2.929 13.07l1.06-1.06M12.01 3.99l1.06-1.06" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>,
};

const KIDS_LEADS = [
  { name: 'Mal Carter', email: 'mal@thebelonging.co', programs: 'All Kids', role: 'Admin', status: 'active' },
  { name: 'Jordan Lee', email: 'jordan.lee@email.com', programs: 'Elementary', role: 'Team Lead', status: 'active' },
  { name: 'Sam Rivera', email: 'sam.rivera@email.com', programs: '5-6', role: 'Team Lead', status: 'pending' },
  { name: 'Avery Cole', email: 'avery.cole@email.com', programs: '3-4', role: 'Team Lead', status: 'not-invited' },
];
// Detail Sheets: embedded Google Docs, categorized by program + session
// (reuses the main app's docs-embed pattern).
const DETAIL_SHEETS = [
  { program: 'Elementary', session: 'Thursday PM', label: 'Elementary — Thursday PM' },
  { program: 'Elementary', session: 'Friday AM', label: 'Elementary — Friday AM' },
  { program: '5-6', session: 'Friday AM', label: '5-6 — Friday AM' },
  { program: '3-4', session: 'Friday AM', label: '3-4 — Friday AM' },
];
// Daily Details: auto-built per-block overview (who's serving where + activities
// + notes), one per block, printable. Sample mirrors Mal's Thursday PM sheet.
const DAILY_DETAILS = {
  'Thursday PM': {
    positions: [
      { role: '2iC', people: ['Cassie', 'Kourtney'] },
      { role: 'Speaker', people: ['Keithon'] },
      { role: 'Check In', people: ['Hannah R'] },
      { role: 'Stage Game', people: ['Cassie', 'Mal'] },
      { role: 'Interpreter', people: ['Bri'] },
      { role: 'Superkids', people: ['Emily S'] },
      { role: 'Party 2iC', people: ['Bekah K'] },
      { role: 'Party Team', people: ['Kevyn', 'Lauren C', 'Oreo', 'Rachel B', 'Stefani'] },
    ],
    smallGroups: [
      { color: 'Green', leaders: ['Daniel', 'Chad', 'Landon'] },
      { color: 'Coral', leaders: ['Nikki', 'Emily F', 'Patience'] },
      { color: 'Yellow', leaders: ['Vlad', 'Matt G', 'Micha'] },
      { color: 'Teal', leaders: ['Emily M', 'Erin V', 'Lauren Y'] },
      { color: 'Blue', leaders: ['Derick', 'Kevan', 'Rob', 'Devon'] },
      { color: 'Pink', leaders: ['Alyx', 'Shelby', 'Tessa'] },
    ],
    helicopters: ['Amber', 'Ashton', 'Avery', 'Carrigan', 'Ciara', 'Graham G', 'Leah R', 'Lilly F', 'Maggie M', 'Maurice B', 'Nate G', 'Paige G', 'Colby', 'Jordan M'],
    preService: ['Journal Making', 'Name Game'],
    stageGame: '—',
    partyActivities: ['Color blocked chips', 'Fizzy drinks', 'Gaga (other tent)', 'Big inflatable main tent', 'Store', 'Legos', 'Key chain making'],
    meetingNotes: ['Structure of the day', 'Yellow tag, allergy', "Don't forget about tickets, but also we only have a limited amount haha", 'After check out, clean!', 'Emergency plan'],
  },
  'Friday AM': {
    positions: [
      { role: '2iC', people: ['Cassie'] },
      { role: 'Speaker', people: ['Keithon'] },
      { role: 'Check In', people: ['Hannah R', 'Riley Fox'] },
      { role: 'Stage Game', people: ['Mal'] },
      { role: 'Rotation Lead', people: ['Casey Kim'] },
    ],
    smallGroups: [
      { color: 'Green', leaders: ['Daniel', 'Chad'] },
      { color: 'Coral', leaders: ['Nikki', 'Emily F'] },
      { color: 'Yellow', leaders: ['Vlad', 'Matt G'] },
      { color: 'Blue', leaders: ['Derick', 'Kevan'] },
    ],
    helicopters: ['Amber', 'Avery', 'Ciara', 'Leah R', 'Nate G'],
    preService: ['Journal Making', 'Coloring'],
    stageGame: 'Minute-to-win-it relay',
    partyActivities: [],
    meetingNotes: ['Rotation timing — keep groups moving', 'Allergy tags: check yellow', 'Morning snack at 10:15'],
  },
};
const KIDS_TEMPLATES = [
  { id: 'schedule-link', name: 'Schedule Link', meta: 'Sent when link is shared', subject: 'Your Kids Conference schedule is ready, {{first_name}}', body: 'Hi {{first_name}},\n\nYour Kids serving schedule is ready — view it anytime with the link below.\n\n{{schedule_link_button}}\n\nThank you for serving our kids!\n\n— The Belonging Co Kids Team' },
  { id: 'availability', name: 'Availability Request', meta: 'Sent to gather availability', subject: 'Which Kids sessions can you serve, {{first_name}}?', body: 'Hi {{first_name}},\n\nTell us how many sessions you can serve and which ones you’re free for.\n\n{{availability_button}}\n\n— The Belonging Co Kids Team' },
  { id: 'roster-confirmation', name: 'Roster Confirmation', meta: 'Sent when added to a program', subject: 'You’re on the {{program}} team, {{first_name}}!', body: 'Hi {{first_name}},\n\nYou’ve been added to the {{program}} Kids program. We’re so glad you’re here!\n\n— The Belonging Co Kids Team' },
];

// Multi-select role pill dropdown — mirrors the real Team Lead People tab.
function DemoRoleSelect({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({});
  const ref = useRef();
  const has = value.length > 0;
  function toggle() {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ position: 'fixed', top: r.bottom + 4, left: r.left });
    }
    setOpen(o => !o);
  }
  function tog(role) { onChange(value.includes(role) ? value.filter(r => r !== role) : [...value, role]); }
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={toggle} style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, padding: '4px 8px 4px 10px', borderRadius: 20, whiteSpace: 'nowrap', border: `1px solid ${has ? '#4F8475' : 'var(--border)'}`, background: has ? TEAL : 'var(--off)', color: has ? '#fff' : 'var(--muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, maxWidth: 190, overflow: 'hidden' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{has ? value.join(', ') : '— no group —'}</span>
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}><path d="M1.5 2.5L4 5L6.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={{ ...pos, zIndex: 50, background: '#fff', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', minWidth: 160, overflow: 'hidden' }}>
            {options.map(r => {
              const c = value.includes(r);
              return (
                <div key={r} onClick={() => tog(r)} style={{ padding: '9px 14px', cursor: 'pointer', fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: c ? 700 : 500, color: c ? '#2E5A4E' : 'var(--mid)', background: c ? 'rgba(106,162,146,0.12)' : '#fff', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--border)' }}>
                  <span style={{ width: 14, height: 14, flexShrink: 0, borderRadius: 3, border: `1.5px solid ${c ? TEAL : 'var(--border)'}`, background: c ? TEAL : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {c && <svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </span>
                  {r}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Branded single-select dropdown (teal) — replaces native <select> so every
// menu in the demo is on-brand.
function BrandSelect({ value, options, onChange, placeholder = '—', width, size = 'md', pill = false }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({});
  const ref = useRef();
  function toggle() {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ position: 'fixed', top: r.bottom + 4, left: r.left, minWidth: Math.max(r.width, 120) });
    }
    setOpen(o => !o);
  }
  const pad = size === 'sm' ? '5px 8px' : '7px 12px';
  // Pill variant matches the original pass-level control: white, black outline,
  // rounded, uppercase. The open menu stays the branded teal dropdown.
  const buttonStyle = pill
    ? { fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '4px 12px', borderRadius: 100, border: '1.5px solid var(--black)', background: '#fff', color: value ? 'var(--black)' : 'var(--muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }
    : { width: width ? '100%' : undefined, fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, padding: pad, borderRadius: 6, border: `1.5px solid ${open ? TEAL : 'var(--border)'}`, background: '#fff', color: value ? 'var(--black)' : 'var(--muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 };
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', width: pill ? undefined : width }}>
      <button onClick={toggle} style={buttonStyle}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || placeholder}</span>
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}><path d="M1.5 2.5L4 5L6.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={{ ...pos, zIndex: 50, background: '#fff', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
            {options.map(o => {
              const c = o === value;
              return (
                <div key={o || '—'} onClick={() => { onChange(o); setOpen(false); }} style={{ padding: '8px 14px', cursor: 'pointer', fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: c ? 700 : 500, color: c ? '#2E5A4E' : 'var(--mid)', background: c ? 'rgba(106,162,146,0.12)' : '#fff', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                  {o || placeholder}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Color picker swatch — preset grid + custom color input. Used to set group colors.
function ColorSwatch({ color, onChange, size = 26 }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({});
  const ref = useRef();
  function toggle() {
    if (!open && ref.current) { const r = ref.current.getBoundingClientRect(); setPos({ position: 'fixed', top: r.bottom + 6, left: r.left }); }
    setOpen(o => !o);
  }
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={toggle} title="Change color" style={{ width: size, height: size, borderRadius: '50%', background: color, border: '2px solid #fff', boxShadow: '0 0 0 1px var(--border)', cursor: 'pointer', flexShrink: 0 }} />
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={{ ...pos, zIndex: 50, background: '#fff', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.14)', padding: 12, width: 168 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 10 }}>
              {COLOR_PRESETS.map(c => (
                <button key={c} onClick={() => { onChange(c); setOpen(false); }} style={{ width: 24, height: 24, borderRadius: '50%', background: c, border: color === c ? '2px solid #202020' : '2px solid #fff', boxShadow: '0 0 0 1px var(--border)', cursor: 'pointer' }} />
              ))}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              <input type="color" value={color} onChange={e => onChange(e.target.value)} style={{ width: 28, height: 28, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} />
              Custom
            </label>
          </div>
        </>
      )}
    </div>
  );
}

// Editable group row: color swatch + name (commits on blur/Enter) + remove.
function GroupRow({ name, color, onRename, onRecolor, onRemove }) {
  const [v, setV] = useState(name);
  useEffect(() => setV(name), [name]);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <ColorSwatch color={color} onChange={onRecolor} />
      <input className="form-input" value={v} onChange={e => setV(e.target.value)} onBlur={() => onRename(v)} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} style={{ flex: 1, fontSize: 13 }} />
      <button className="btn-remove" title="Remove group" onClick={onRemove}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
      </button>
    </div>
  );
}

// Inline-editable list (add / edit-in-place / remove) for Daily Details sections.
function EditableList({ items, onChange, placeholder }) {
  const [val, setVal] = useState('');
  const addItem = () => { if (val.trim()) { onChange([...items, val.trim()]); setVal(''); } };
  return (
    <div>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <input className="form-input" value={it} onChange={e => { const n = [...items]; n[i] = e.target.value; onChange(n); }} style={{ flex: 1, fontSize: 13 }} />
          <button className="btn-remove" title="Remove" onClick={() => onChange(items.filter((_, j) => j !== i))}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
          </button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6 }}>
        <input className="form-input" placeholder={placeholder || 'Add item…'} value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addItem(); }} style={{ flex: 1, fontSize: 13 }} />
        <button className="btn btn-sm btn-dark" onClick={addItem} disabled={!val.trim()}>Add</button>
      </div>
    </div>
  );
}

// ── Mal admin content ────────────────────────────────────────────────────────
function MalOverview({ setView }) {
  const total = KIDS_PEOPLE.length;
  const rostered = KIDS_PEOPLE.filter(p => p.status === 'accepted').length;
  const byProgram = PROGRAMS.map(pr => ({ program: pr, count: KIDS_PEOPLE.filter(p => p.program === pr).length }));

  return (
    <div className="page-content">
      <div className="page-title-row">
        <div>
          <div className="page-title">Kids Overview</div>
          <div className="page-sub">The Belonging Co Kids Conference · 2026</div>
        </div>
        <button className="btn btn-dark">+ New Session</button>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="stat-label">Kids SLs</div><div className="stat-value">{total}</div><div className="stat-sub">across 3 programs</div><div className="stat-bar"><div className="stat-bar-fill" style={{ width: '70%', background: TEAL }} /></div></div>
        <div className="stat-card"><div className="stat-label">Rostered</div><div className="stat-value">{rostered}</div><div className="stat-sub">{total - rostered} not yet accepted</div><div className="stat-bar"><div className="stat-bar-fill" style={{ width: `${(rostered / total) * 100}%`, background: TEAL }} /></div></div>
        <div className="stat-card"><div className="stat-label">Programs</div><div className="stat-value">3</div><div className="stat-sub">Elementary · 5-6 · 3-4</div></div>
        <div className="stat-card"><div className="stat-label">Need Radio</div><div className="stat-value">6</div><div className="stat-sub">Flagged across Kids</div></div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <div className="section-header">Program Breakdown</div>
        <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex' }}>
            {byProgram.map((item, i) => (
              <div key={item.program} style={{ flex: 1, padding: '16px 20px', borderRight: i < byProgram.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>{item.program}</div>
                <div style={{ fontFamily: 'var(--font-h)', fontSize: 26, fontWeight: 800, color: 'var(--black)', lineHeight: 1 }}>{item.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="two-col-equal">
        <div>
          <div className="section-header">Quick Actions</div>
          <div className="panel" style={{ display: 'flex', flexDirection: 'column' }}>
            {[
              { label: 'People.', view: 'people' },
              { label: 'Rotations.', view: 'rotations' },
              { label: 'Meal Totals.', view: 'meals' },
              { label: 'Team Lead Access.', view: 'access' },
              { label: 'Settings.', view: 'settings' },
            ].map(item => (
              <div key={item.view} className="quick-link" style={{ flex: 1 }} onClick={() => setView(item.view)}>
                <span className="quick-link-name">{item.label}</span><span style={{ color: 'var(--muted)' }}>→</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="section-header">Kids Meal Totals (union rule)</div>
          <div className="panel">
            <table className="data-table">
              <thead><tr><th>Day</th><th>Meal</th><th>People</th></tr></thead>
              <tbody>{MEAL_TOTALS.map((r, i) => <tr key={i} style={{ cursor: 'default' }}><td className="td-name">{r.day}</td><td>{r.meal}</td><td>{r.count}</td></tr>)}</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function MealsPage() {
  return (
    <div className="page-content">
      <div className="page-title-row"><div><div className="page-title">Meal Totals</div><div className="page-sub">Union rule · everyone serving Kids, counted once</div></div></div>
      <div className="panel">
        <table className="data-table">
          <thead><tr><th>Day</th><th>Meal</th><th>People</th></tr></thead>
          <tbody>{MEAL_TOTALS.map((r, i) => <tr key={i} style={{ cursor: 'default' }}><td className="td-name">{r.day}</td><td>{r.meal}</td><td>{r.count}</td></tr>)}</tbody>
        </table>
      </div>
      <div className="notice-bar" style={{ marginTop: 16 }}>Morning-only → no meal · evening-only → dinner · all-day → lunch + dinner. Both-servers also earn any meal their main blocks overlap, deduped to one ticket.</div>
    </div>
  );
}

// Detail Sheets (Kids) — embedded Google Docs, categorized by program + session.
function DetailSheetsPage({ toast }) {
  const [openId, setOpenId] = useState(null);
  const open = DETAIL_SHEETS.find(d => d.label === openId);
  if (open) return (
    <div className="page-content">
      <div className="page-title-row">
        <div><div className="page-title">{open.label}</div><div className="page-sub">Detail sheet · embedded Google Doc</div></div>
        <button className="btn btn-sm" onClick={() => setOpenId(null)}>← Back</button>
      </div>
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ background: TEAL, color: '#fff', padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700 }}>{open.label}</span>
          <span style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, opacity: 0.85 }}>Open in Drive ↗</span>
        </div>
        <div style={{ height: 420, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off)', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>(Google Doc embeds here)</div>
      </div>
    </div>
  );
  return (
    <div className="page-content">
      <div className="page-title-row">
        <div><div className="page-title">Detail Sheets</div><div className="page-sub">Embedded Google Docs · by program &amp; session</div></div>
        <button className="btn btn-dark" onClick={() => toast('Add Detail Sheet (demo)')}>+ Add Detail Sheet</button>
      </div>
      {PROGRAMS.map(pr => {
        const rows = DETAIL_SHEETS.filter(d => d.program === pr);
        if (!rows.length) return null;
        return (
          <div key={pr} style={{ marginBottom: 24 }}>
            <div className="section-header">{pr}</div>
            <div className="panel">
              {rows.map((d, i) => (
                <div key={d.label} className="quick-link" style={{ borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none' }} onClick={() => setOpenId(d.label)}>
                  <span style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 800, color: 'var(--black)' }}>{d.session}</span>
                  <span style={{ color: 'var(--muted)' }}>Open →</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Daily Details (Kids) — auto-built per-block overview + print.
function DailyDetailsPage() {
  const blocks = Object.keys(DAILY_DETAILS);
  const [block, setBlock] = useState(blocks[0]);
  const base = DAILY_DETAILS[block];
  // Editable sections (Mal can edit these in-app); reset when the block changes.
  // `editing` holds the one section key currently in edit mode (null = all read-only).
  const [editing, setEditing] = useState(null);
  const [ed, setEd] = useState(() => {
    const b = DAILY_DETAILS[blocks[0]];
    return { preService: [...b.preService], stageGame: b.stageGame, partyActivities: [...b.partyActivities], meetingNotes: [...b.meetingNotes] };
  });
  useEffect(() => {
    const b = DAILY_DETAILS[block];
    setEd({ preService: [...b.preService], stageGame: b.stageGame, partyActivities: [...b.partyActivities], meetingNotes: [...b.meetingNotes] });
    setEditing(null);
  }, [block]);
  const setF = (k, v) => setEd(p => ({ ...p, [k]: v }));
  const PENCIL = <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8.5 1.5l2 2L3 11H1v-2L8.5 1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></svg>;
  const editHeader = (title, k) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <div className="section-header" style={{ marginBottom: 0 }}>{title}</div>
      <button className="btn-ghost" style={{ padding: '3px 9px', fontSize: 11 }} onClick={() => setEditing(editing === k ? null : k)}>
        {editing === k ? 'Done' : PENCIL}
      </button>
    </div>
  );
  const renderList = (title, k) => (
    <div style={{ marginBottom: 22 }}>
      {editHeader(title, k)}
      {editing === k
        ? <EditableList items={ed[k]} onChange={v => setF(k, v)} />
        : (ed[k].length
          ? <ul style={{ margin: 0, paddingLeft: 20 }}>{ed[k].map((x, i) => <li key={i} style={{ fontFamily: 'var(--font-b)', fontSize: 14, color: 'var(--mid)', marginBottom: 4 }}>{x}</li>)}</ul>
          : <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>—</div>)}
    </div>
  );

  function printSheet() {
    const esc = s => String(s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const list = arr => arr && arr.length ? '<ul>' + arr.map(x => `<li>${esc(x)}</li>`).join('') + '</ul>' : '<p class="dash">—</p>';
    const chips = base.smallGroups.map(g => {
      const c = colorFor(g.color), t = textOn(c);
      return `<div class="g"><div class="gh" style="background:${c};color:${t}">${esc(g.color)}</div><div class="gb">${g.leaders.map(esc).join('<br>')}</div></div>`;
    }).join('');
    const hd = `<div class="hd"><div><h1>Daily Details</h1><div class="sub">${esc(block)}</div></div><div class="org">The Belonging Co<br>Kids Conference 2026</div></div>`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Daily Details — ${esc(block)}</title>
      <link href="https://fonts.googleapis.com/css2?family=Archivo:wght@400;700;800;900&family=Lato:wght@300;400;700&display=swap" rel="stylesheet">
      <style>
      @page{margin:0}
      *{box-sizing:border-box}
      body{font-family:'Lato',sans-serif;color:#202020;margin:0;background:#fff}
      .page{padding-bottom:40px}
      .page.brk{page-break-before:always;break-before:page}
      .wrap{padding:0 40px}
      .hd{background:#6AA292;color:#fff;display:flex;justify-content:space-between;align-items:flex-end;padding:20px 28px;margin-bottom:22px}
      .hd h1{font-family:'Archivo';font-weight:900;font-size:24px;margin:0}
      .hd .sub{font-family:'Archivo';font-weight:700;font-size:11px;letter-spacing:.14em;text-transform:uppercase;opacity:.85}
      .hd .org{font-family:'Archivo';font-weight:700;font-size:11px;letter-spacing:.14em;text-transform:uppercase;opacity:.85;text-align:right}
      h2{font-family:'Archivo';font-weight:800;font-size:12px;letter-spacing:.16em;text-transform:uppercase;color:#6AA292;border-bottom:2px solid #E0DED9;padding-bottom:6px;margin:24px 0 12px}
      h2:first-child{margin-top:0}
      table{width:100%;border-collapse:collapse}
      td{padding:7px 8px;border-bottom:1px solid #eee;font-size:14px;vertical-align:top}
      td.r{font-family:'Archivo';font-weight:700;width:160px;color:#202020}
      .cols{display:flex;flex-wrap:wrap;gap:12px}
      .g{border:1px solid #E0DED9;border-radius:10px;overflow:hidden;min-width:150px}
      .gh{font-family:'Archivo';font-weight:800;font-size:12px;letter-spacing:.06em;padding:7px 12px}
      .gb{padding:9px 12px;font-size:14px;line-height:1.5}
      .heli{border:1px solid #E0DED9;border-radius:10px;padding:12px 16px;column-count:3;column-gap:24px}
      .heli div{font-size:14px;margin:3px 0;-webkit-column-break-inside:avoid;break-inside:avoid}
      ul{margin:6px 0;padding-left:20px}li{font-size:14px;margin:3px 0}
      .dash{color:#999}
      @media print{.hd,.gh{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
      </style></head><body>
      <div class="page">
        ${hd}
        <div class="wrap">
          <h2>Team</h2><table>${base.positions.map(p => `<tr><td class="r">${esc(p.role)}</td><td>${p.people.map(esc).join(', ')}</td></tr>`).join('')}</table>
          <h2>Small Groups</h2><div class="cols">${chips}</div>
          <h2>Helicopters</h2><div class="heli">${base.helicopters.map(n => `<div>${esc(n)}</div>`).join('')}</div>
        </div>
      </div>
      <div class="page brk">
        ${hd}
        <div class="wrap">
          <h2>Pre-Service Activities</h2>${list(ed.preService)}
          <h2>Stage Game</h2><p${ed.stageGame ? '' : ' class="dash"'}>${esc(ed.stageGame || '—')}</p>
          <h2>Party Activities</h2>${list(ed.partyActivities)}
          <h2>Meeting Notes</h2>${list(ed.meetingNotes)}
        </div>
      </div>
      </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400); }
  }

  return (
    <div className="page-content">
      <div className="page-title-row" style={{ marginBottom: 16 }}>
        <div><div className="page-title">Daily Details</div><div className="page-sub">One-click overview for each block · who's serving where</div></div>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 24 }}>
        <BrandSelect value={block} options={blocks} onChange={setBlock} />
        <button className="btn btn-dark" onClick={printSheet}>Print</button>
      </div>

      <div className="two-col" style={{ gridTemplateColumns: '1fr 1fr', alignItems: 'start' }}>
        <div className="panel">
          <div className="panel-header"><div className="panel-title">Team</div></div>
          <table className="data-table">
            <tbody>{base.positions.map(p => (
              <tr key={p.role} style={{ cursor: 'default' }}>
                <td className="td-name" style={{ width: 150 }}>{p.role}</td>
                <td>{p.people.join(', ')}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <div>
          <div className="section-header">Small Groups</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
            {base.smallGroups.map(g => {
              const c = colorFor(g.color);
              return (
                <div key={g.color} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                  <div style={{ background: c, color: textOn(c), fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', padding: '6px 12px' }}>{g.color}</div>
                  <div style={{ padding: '10px 12px' }}>
                    {g.leaders.map(n => <div key={n} style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--mid)' }}>{n}</div>)}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="section-header">Helicopters</div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', background: '#fff', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', columnGap: 16, rowGap: 4 }}>
            {base.helicopters.map(n => <div key={n} style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--mid)' }}>{n}</div>)}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 8, maxWidth: 640 }}>
        {renderList('Pre-Service Activities', 'preService')}
        <div style={{ marginBottom: 22 }}>
          {editHeader('Stage Game', 'stageGame')}
          {editing === 'stageGame'
            ? <input className="form-input" value={ed.stageGame} onChange={e => setF('stageGame', e.target.value)} placeholder="e.g. Minute-to-win-it relay" style={{ fontSize: 13, maxWidth: 360 }} />
            : <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, color: ed.stageGame ? 'var(--mid)' : 'var(--muted)' }}>{ed.stageGame || '—'}</div>}
        </div>
        {renderList('Party Activities', 'partyActivities')}
        {renderList('Meeting Notes', 'meetingNotes')}
      </div>
    </div>
  );
}

// Team Lead Access (Kids) — mirrors the real admin access table.
function KidsAccessPage({ toast }) {
  const statusCell = s => (
    s === 'active' ? <><span className="status-dot dot-green" />Active</>
      : s === 'pending' ? <><span className="status-dot dot-amber" />Invite Pending</>
        : <><span className="status-dot dot-gray" />Not Invited</>
  );
  return (
    <div className="page-content">
      <div className="page-title-row">
        <div><div className="page-title">Team Lead Access</div><div className="page-sub">Who can log in to Kids and what they see</div></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-sm" onClick={() => toast('Invite All (demo)')}>Invite All</button>
          <button className="btn btn-dark" onClick={() => toast('Add Team Lead (demo)')}>+ Add Team Lead</button>
        </div>
      </div>
      <div className="panel">
        <table className="data-table">
          <thead><tr><th>Name</th><th>Google Account</th><th>Program(s)</th><th>Role</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {KIDS_LEADS.map(l => (
              <tr key={l.email} style={{ cursor: 'default' }}>
                <td><span className="td-name">{l.name}</span></td>
                <td>{l.email}</td>
                <td>{l.programs}</td>
                <td>{l.role === 'Admin' ? <span className="pill pill-dark">Admin</span> : <span className="pill pill-gray">Team Lead</span>}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{statusCell(l.status)}</td>
                <td><button className="btn btn-sm" onClick={() => toast(`Edit ${l.name} (demo)`)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// SL Manual (Kids) — upload or link a manual; mirrors the real chooser.
function KidsManualPage({ toast }) {
  const [link, setLink] = useState('');
  const card = { background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: 28, textAlign: 'center' };
  const iconWrap = { width: 48, height: 48, background: 'var(--off)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' };
  return (
    <div className="page-content">
      <div className="page-title-row"><div><div className="page-title">Kids SL Manual</div><div className="page-sub">Servant Leader manual · visible to all Kids servant leaders</div></div></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 720 }}>
        <div style={card}>
          <div style={iconWrap}><svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 14V4M7 8l4-4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M3 16v1a2 2 0 002 2h12a2 2 0 002-2v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg></div>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 15, fontWeight: 800, marginBottom: 6 }}>Upload a PDF</div>
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', marginBottom: 18 }}>Upload the Kids SL Manual from your device</div>
          <button className="btn btn-sm" onClick={() => toast('Choose file (demo)')}>Choose File</button>
        </div>
        <div style={{ ...card, textAlign: 'left' }}>
          <div style={iconWrap}><svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 15, fontWeight: 800, marginBottom: 6, textAlign: 'center' }}>Paste a Google Drive Link</div>
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', marginBottom: 18, textAlign: 'center' }}>PDF or Google Doc share link</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="form-input" placeholder="https://drive.google.com/…" value={link} onChange={e => setLink(e.target.value)} style={{ flex: 1, fontSize: 12 }} />
            <button className="btn btn-sm btn-dark" onClick={() => toast('Manual link saved (demo)')} disabled={!link.trim()}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Settings (Kids) — Email Templates.
function KidsSettingsPage({ toast }) {
  const [tplId, setTplId] = useState(KIDS_TEMPLATES[0].id);
  const tpl = KIDS_TEMPLATES.find(t => t.id === tplId);
  return (
    <div className="page-content">
      <div className="page-title-row"><div><div className="page-title">Settings</div><div className="page-sub">Email Templates</div></div></div>
      <div className="two-col" style={{ gridTemplateColumns: '240px 1fr', alignItems: 'start' }}>
        <div>
          <div className="section-header" style={{ marginBottom: 10 }}>All Templates</div>
          <div className="panel">
            {KIDS_TEMPLATES.map(t => (
              <div key={t.id} className={`email-tpl-item${t.id === tplId ? ' active' : ''}`} onClick={() => setTplId(t.id)}>
                <div className="email-tpl-name">{t.name}</div>
                <div className="email-tpl-meta">{t.meta}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="panel" style={{ padding: 24 }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 17, fontWeight: 800, marginBottom: 16 }}>{tpl.name}</div>
          <div className="form-group"><label className="form-label">Subject</label><input className="form-input" defaultValue={tpl.subject} key={tpl.id + 's'} /></div>
          <div className="form-group"><label className="form-label">Body</label><textarea className="form-input" rows={9} defaultValue={tpl.body} key={tpl.id + 'b'} style={{ resize: 'vertical', fontFamily: 'var(--font-b)', fontWeight: 300 }} /></div>
          <button className="btn btn-dark" onClick={() => toast('Template saved (demo)')}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

function ContributingPage() {
  const DAY_ORDER = ['Friday', 'Saturday', 'Sunday'];
  const bucket = start => { const h = parseInt((start || '').split(':')[0], 10); return h < 12 ? 'Morning' : h < 17 ? 'Afternoon' : 'Evening'; };
  const BUCKETS = ['Morning', 'Afternoon', 'Evening'];

  // Flatten every contributing block with its team, then group by day → service
  // time so all teams serving the same slot sit together (block names differ
  // across teams, so time is the reliable way to line them up).
  const all = [];
  CONTRIB_TEAMS.forEach(t => t.blocks.forEach(b => all.push({ ...b, team: t.team })));
  const days = DAY_ORDER.filter(d => all.some(b => b.day === d));

  return (
    <div className="page-content">
      <div className="page-title-row"><div><div className="page-title">Contributing Teams</div><div className="page-sub">Read-only · grouped by day &amp; service so you can see every team in a slot together</div></div></div>
      {days.map(day => (
        <div key={day} style={{ marginBottom: 28 }}>
          <div className="page-title" style={{ fontSize: 20, marginBottom: 12 }}>{day}</div>
          {BUCKETS.map(bk => {
            const blocks = all.filter(b => b.day === day && bucket(b.start) === bk).sort((a, b) => (a.start || '').localeCompare(b.start || ''));
            if (blocks.length === 0) return null;
            return (
              <div key={bk} style={{ marginBottom: 16 }}>
                <div className="section-header">{bk}</div>
                <div className="panel">
                  {blocks.map((b, i) => (
                    <div key={i} style={{ borderBottom: i < blocks.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div className="block-card-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span className="block-card-title">{b.team}</span>
                          <span className="block-card-meta">{b.title} · {b.time}</span>
                        </div>
                      </div>
                      {b.positions.map((p, j) => (
                        <div key={j} className="block-position-row">
                          <div className="block-pos-label">{p.label}</div>
                          <div className="block-pos-person">
                            {p.open
                              ? <span style={{ color: 'var(--subtle)', fontStyle: 'italic' }}>Open position</span>
                              : <><span className="status-dot dot-green" style={{ marginRight: 8 }} />{p.person}</>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function RotationsPage() {
  const [rot, setRot] = useState(ROT_INIT);
  const [mode, setMode] = useState('day');
  const [nS, setNS] = useState(''); const [nA, setNA] = useState(''); const [nC, setNC] = useState('');
  const [nColor, setNColor] = useState(COLOR_PRESETS[6]);
  const update = mut => setRot(prev => { const n = JSON.parse(JSON.stringify(prev)); mut(n); return n; });
  const setCell = (s, a, g) => update(n => { (n.grid[s] = n.grid[s] || {})[a] = g; });
  function add(kind, val, clear) { const v = val.trim(); if (!v) return; update(n => { if (!n[kind].includes(v)) n[kind].push(v); }); clear(''); }
  function remove(kind, val) {
    update(n => {
      n[kind] = n[kind].filter(x => x !== val);
      if (kind === 'sessions') delete n.grid[val];
      if (kind === 'activities') Object.keys(n.grid).forEach(s => { if (n.grid[s]) delete n.grid[s][val]; });
      if (kind === 'groups') { if (n.groupColors) delete n.groupColors[val]; Object.keys(n.grid).forEach(s => Object.keys(n.grid[s] || {}).forEach(a => { if (n.grid[s][a] === val) n.grid[s][a] = ''; })); }
    });
  }
  function addGroup() { const v = nC.trim(); if (!v) return; update(n => { if (!n.groups.includes(v)) { n.groups.push(v); (n.groupColors = n.groupColors || {})[v] = nColor; } }); setNC(''); }
  const recolor = (name, color) => update(n => { (n.groupColors = n.groupColors || {})[name] = color; });
  function renameGroup(oldN, nextN) {
    const v = (nextN || '').trim();
    if (!v || v === oldN) return;
    update(n => {
      if (n.groups.includes(v)) return;
      n.groups = n.groups.map(g => g === oldN ? v : g);
      if (n.groupColors) { n.groupColors[v] = n.groupColors[oldN]; delete n.groupColors[oldN]; }
      Object.keys(n.grid).forEach(s => Object.keys(n.grid[s] || {}).forEach(a => { if (n.grid[s][a] === oldN) n.grid[s][a] = v; }));
    });
  }
  const gColor = name => (rot.groupColors && rot.groupColors[name]) || colorFor(name);
  const lists = [
    { kind: 'sessions', label: 'Sessions', val: nS, set: setNS, ph: 'e.g. Saturday 2' },
    { kind: 'activities', label: 'Activities', val: nA, set: setNA, ph: 'e.g. Story' },
  ];
  const cell = (s, a) => (rot.grid[s] && rot.grid[s][a]) || '—';
  // Derive the day from a session name ("Friday 1" → "Friday") so Day view can
  // group sessions even though days aren't a separate field.
  const dayOf = s => (s.replace(/\s+\d+\s*$/, '').trim() || s);
  const days = [];
  rot.sessions.forEach(s => { const d = dayOf(s); if (!days.includes(d)) days.push(d); });

  const TABS = [
    { id: 'day', label: 'Day view' },
    { id: 'group', label: 'Group view' },
    { id: 'activity', label: 'Activity view' },
    { id: 'build', label: 'Build' },
  ];
  const pillHdr = label => <span className="pill" style={{ background: TEAL, color: '#fff', display: 'inline-block', marginBottom: 12 }}>{label}</span>;
  const empty = rot.sessions.length === 0 || rot.activities.length === 0;

  // Card + grid styling for the read views: each group/activity/day is its own
  // outlined, rounded card, and the cards flow into multiple columns.
  const gridStyle = min => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 16, alignItems: 'start' });
  const cardStyle = { border: '1px solid var(--border)', borderRadius: 8, padding: '16px', background: '#fff' };
  const mTh = { fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', textAlign: 'left', padding: '0 10px 8px 0', borderBottom: '1px solid var(--border)' };
  const mTd = { fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--mid)', padding: '8px 10px 8px 0', borderBottom: '1px solid var(--border)' };
  const mTdName = { ...mTd, fontFamily: 'var(--font-h)', fontWeight: 700, color: 'var(--black)' };

  return (
    <div className="page-content">
      <div className="page-title-row"><div><div className="page-title">Rotations</div><div className="page-sub">Which group is at which activity each session</div></div></div>
      <div className="panel">
        <div className="panel-header" style={{ gap: 8, flexWrap: 'wrap' }}>
          <div className="panel-title">Elementary</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {TABS.map(t => <button key={t.id} className={`tv-tab${mode === t.id ? ' active' : ''}`} style={{ padding: '6px 12px' }} onClick={() => setMode(t.id)}>{t.label}</button>)}
          </div>
        </div>
        <div style={{ padding: '20px 24px', overflowX: 'auto' }}>
          {mode === 'build' ? (
            <>
              <div className="two-col" style={{ gridTemplateColumns: '1fr 1fr 1fr', marginBottom: 24 }}>
                {lists.map(col => (
                  <div key={col.kind}>
                    <div className="form-label">{col.label}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                      {rot[col.kind].map(v => (
                        <span key={v} className="pill pill-gray" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {v}<span style={{ cursor: 'pointer', fontWeight: 800 }} onClick={() => remove(col.kind, v)}>×</span>
                        </span>
                      ))}
                      {rot[col.kind].length === 0 && <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>None yet</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="form-input" style={{ fontSize: 13 }} placeholder={col.ph} value={col.val} onChange={e => col.set(e.target.value)} onKeyDown={e => e.key === 'Enter' && add(col.kind, col.val, col.set)} />
                      <button className="btn btn-sm btn-dark" onClick={() => add(col.kind, col.val, col.set)} disabled={!col.val.trim()}>Add</button>
                    </div>
                  </div>
                ))}
                {/* Groups column: name + color, editable */}
                <div>
                  <div className="form-label">Groups</div>
                  <div style={{ marginBottom: 8 }}>
                    {rot.groups.map(g => (
                      <GroupRow key={g} name={g} color={gColor(g)} onRename={next => renameGroup(g, next)} onRecolor={c => recolor(g, c)} onRemove={() => remove('groups', g)} />
                    ))}
                    {rot.groups.length === 0 && <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>None yet</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <ColorSwatch color={nColor} onChange={setNColor} />
                    <input className="form-input" style={{ fontSize: 13, flex: 1 }} placeholder="e.g. Yellow" value={nC} onChange={e => setNC(e.target.value)} onKeyDown={e => e.key === 'Enter' && addGroup()} />
                    <button className="btn btn-sm btn-dark" onClick={addGroup} disabled={!nC.trim()}>Add</button>
                  </div>
                </div>
              </div>
              {rot.sessions.length > 0 && rot.activities.length > 0 ? (
                <table className="data-table" style={{ minWidth: 420 }}>
                  <thead><tr><th>Session</th>{rot.activities.map(a => <th key={a}>{a}</th>)}</tr></thead>
                  <tbody>{rot.sessions.map(s => (
                    <tr key={s} style={{ cursor: 'default' }}><td className="td-name">{s}</td>{rot.activities.map(a => (
                      <td key={a}><BrandSelect value={(rot.grid[s] && rot.grid[s][a]) || ''} options={['', ...rot.groups]} onChange={g => setCell(s, a, g)} placeholder="—" size="sm" width={110} /></td>
                    ))}</tr>
                  ))}</tbody>
                </table>
              ) : (
                <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>Add at least one session and one activity to build the grid.</div>
              )}
            </>
          ) : empty ? (
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>Build the rotation first.</div>
          ) : mode === 'day' ? (
            // One card per day: a mini grid of that day's sessions × activities → group.
            <div style={gridStyle(300)}>
              {days.map(d => {
                const ds = rot.sessions.filter(s => dayOf(s) === d);
                return (
                  <div key={d} style={cardStyle}>
                    {pillHdr(d)}
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr><th style={mTh}>Session</th>{rot.activities.map(a => <th key={a} style={mTh}>{a}</th>)}</tr></thead>
                        <tbody>{ds.map(s => (
                          <tr key={s}><td style={mTdName}>{s}</td>{rot.activities.map(a => <td key={a} style={mTd}>{cell(s, a)}</td>)}</tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : mode === 'group' ? (
            // One card per group: where that group goes each session.
            <div style={gridStyle(220)}>
              {rot.groups.map(g => (
                <div key={g} style={cardStyle}>
                  {pillHdr(g)}
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={mTh}>Session</th><th style={mTh}>Activity</th></tr></thead>
                    <tbody>{rot.sessions.map(s => (
                      <tr key={s}><td style={mTdName}>{s}</td><td style={mTd}>{rot.activities.find(a => rot.grid[s] && rot.grid[s][a] === g) || '—'}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              ))}
            </div>
          ) : (
            // One card per activity: which group is at that station each session.
            <div style={gridStyle(220)}>
              {rot.activities.map(a => (
                <div key={a} style={cardStyle}>
                  {pillHdr(a)}
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={mTh}>Session</th><th style={mTh}>Group</th></tr></thead>
                    <tbody>{rot.sessions.map(s => (
                      <tr key={s}><td style={mTdName}>{s}</td><td style={mTd}>{cell(s, a)}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Shared Kids team views (mirror the real Team Lead View) ──────────────────
function PassCell({ value }) {
  const [v, setV] = useState(value);
  return <BrandSelect value={v} options={['Kids Pass', 'SL', 'AA Pass', 'Media Pass']} onChange={setV} placeholder="—" pill />;
}

function KidsPeoplePage({ toast }) {
  const [pillFilter, setPillFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('');
  const [radio, setRadio] = useState(() => new Set(KIDS_PEOPLE.filter(p => p.radio).map(p => p.name)));
  const [roleMap, setRoleMap] = useState(() => Object.fromEntries(KIDS_PEOPLE.map(p => [p.name, [p.program]])));

  const scoped = roleFilter ? KIDS_PEOPLE.filter(p => (roleMap[p.name] || []).includes(roleFilter)) : KIDS_PEOPLE;
  const chips = {
    all: scoped.length,
    registered: scoped.filter(p => p.registered).length,
    pending: scoped.filter(p => !p.registered).length,
    unplaced: scoped.filter(p => p.status === 'unplaced').length,
  };
  const rows = scoped.filter(p => {
    if (pillFilter === 'registered' && !p.registered) return false;
    if (pillFilter === 'pending' && p.registered) return false;
    if (pillFilter === 'unplaced' && p.status !== 'unplaced') return false;
    return true;
  });
  const dot = s => s === 'accepted' ? 'dot-green' : s === 'pending' ? 'dot-amber' : 'dot-gray';

  return (
    <>
      <div style={{ padding: '24px 40px 16px', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
        <div className="page-title-row" style={{ marginBottom: 16 }}>
          <div><div className="page-title">Kids</div><div className="page-sub">2026</div></div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-sm" onClick={() => toast('Send Schedule Links (demo)')}>Send Schedule Links</button>
            <button className="btn btn-sm" onClick={() => toast('Email Team (demo)')}>Email Team</button>
            <button className="btn btn-dark" onClick={() => toast('Add Person (demo)')}>+ Add Person</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'on roster', num: chips.all },
            { key: 'registered', label: 'registered', num: chips.registered },
            { key: 'pending', label: 'not yet registered', num: chips.pending },
            { key: 'unplaced', label: 'not yet placed', num: chips.unplaced },
          ].map(f => (
            <button key={f.key} className={`stat-chip${f.key !== 'all' ? ` ${f.key}` : ''}${pillFilter === f.key ? ' active-chip' : ''}`} onClick={() => setPillFilter(f.key)}>
              <span className="stat-chip-num">{f.num}</span> {f.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
          <span style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', alignSelf: 'center', marginRight: 2 }}>Role</span>
          <button className={`stat-chip${!roleFilter ? ' active-chip' : ''}`} onClick={() => setRoleFilter('')}>All</button>
          {PROGRAMS.map(pr => (
            <button key={pr} className={`stat-chip${roleFilter === pr ? ' active-chip' : ''}`} onClick={() => setRoleFilter(roleFilter === pr ? '' : pr)}>
              <span className="stat-chip-num">{KIDS_PEOPLE.filter(p => (roleMap[p.name] || []).includes(pr)).length}</span> {pr}
            </button>
          ))}
        </div>
      </div>

      <div className="page-content" style={{ paddingTop: 24 }}>
        <div className="panel">
          <table className="data-table">
            <thead><tr><th>Name</th><th>Registration</th><th>Pass Level</th><th>Status</th><th>Role</th><th>Schedule</th><th>Radio</th><th></th></tr></thead>
            <tbody>
              {rows.map((p, i) => (
                <tr key={i} style={{ cursor: 'default' }}>
                  <td><span className="td-name">{p.name}</span><br /><span style={{ fontSize: 12, color: 'var(--muted)' }}>{p.email}</span></td>
                  <td>{p.registered
                    ? <span className="pill" style={{ fontSize: 9, padding: '3px 9px', background: '#FFDB7C', color: '#202020' }}>Registered</span>
                    : <span className="pill" style={{ fontSize: 9, padding: '3px 9px', background: '#FF9B71', color: '#202020' }}>Not Registered</span>}</td>
                  <td><PassCell value={p.passLevel} /></td>
                  <td style={{ whiteSpace: 'nowrap' }}><span className={`status-dot ${dot(p.status)}`} />{p.statusLabel}</td>
                  <td><DemoRoleSelect value={roleMap[p.name] || []} options={PROGRAMS} onChange={next => setRoleMap(m => ({ ...m, [p.name]: next }))} /></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {p.status !== 'unplaced'
                      ? <button className="copy-link-btn" onClick={() => toast(`Link copied for ${p.name} (demo)`)}>Copy Link</button>
                      : <span style={{ fontSize: 12, color: 'var(--subtle)' }}>Not yet generated</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={radio.has(p.name)} onChange={() => setRadio(prev => { const n = new Set(prev); n.has(p.name) ? n.delete(p.name) : n.add(p.name); return n; })} style={{ accentColor: '#202020' }} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button className="btn-remove" title="Remove from roster" onClick={() => toast(`Remove ${p.name} (demo)`)}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function KidsRosterPage({ program, toast }) {
  const [filter, setFilter] = useState(null);
  const blocks = KIDS_BLOCKS[program] || [];
  const dot = s => s === 'accepted' ? 'dot-green' : s === 'pending' ? 'dot-amber' : 'dot-gray';
  return (
    <>
      <div style={{ padding: '24px 40px 20px', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
        <div className="page-title-row" style={{ marginBottom: 0 }}>
          <div><div className="page-title">{program} Roster</div><div className="page-sub">Kids · 2026</div></div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm btn-amber" onClick={() => toast('Send Requests (demo)')}>Send Requests</button>
            <button className="btn btn-sm" onClick={() => toast('Edit Block Templates (demo)')}>Edit Block Templates</button>
          </div>
        </div>
      </div>
      <div className="page-content" style={{ paddingTop: 24 }}>
        <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--muted)', marginRight: 4 }}>Filter</span>
          {[
            { key: 'accepted', label: 'Accepted', dotClass: 'bss-accepted', pillClass: 'rsp-accepted' },
            { key: 'pending', label: 'Pending', dotClass: 'bss-pending', pillClass: 'rsp-pending' },
            { key: 'unsent', label: 'Open', dotClass: 'bss-unsent', pillClass: 'rsp-unsent' },
          ].map(f => (
            <button key={f.key} className={`roster-filter-pill ${f.pillClass}${filter === f.key ? ' active' : ''}`} onClick={() => setFilter(filter === f.key ? null : f.key)}>
              <span className={`bss-dot ${f.dotClass}`} />{f.label}
            </button>
          ))}
          {filter && <button className="roster-filter-pill rsp-clear" onClick={() => setFilter(null)}>× Clear</button>}
        </div>
        {blocks.map(block => {
          const positions = filter ? block.positions.filter(p => p.open ? filter === 'unsent' : p.status === filter) : block.positions;
          return (
            <div key={block.id} className="block-card">
              <div className="block-card-header">
                <div><div className="block-card-title">{block.title}</div><div className="block-card-meta">{block.meta}</div></div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-xs" onClick={() => toast('Add position (demo)')}>+ Position</button>
                </div>
              </div>
              {positions.length === 0 && <div style={{ padding: '12px 16px', fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)' }}>No positions matching filter.</div>}
              {positions.map(pos => (
                <div key={pos.id} className="block-position-row">
                  <div className="block-pos-label">{pos.label}</div>
                  <div className="block-pos-person">
                    {pos.open ? (
                      <>
                        <span style={{ color: 'var(--subtle)', fontStyle: 'italic' }}>Open position</span>
                        <button className="btn btn-xs btn-dark" style={{ marginLeft: 10 }} onClick={() => toast(`Fill ${pos.label} (demo)`)}>+ Fill</button>
                      </>
                    ) : (
                      <><span className={`status-dot ${dot(pos.status)}`} style={{ marginRight: 8 }} />{pos.person}</>
                    )}
                  </div>
                  <div className="block-actions" style={{ display: 'flex', gap: 6 }}>
                    {!pos.open && <button className="btn btn-xs" onClick={() => toast(`Change ${pos.label} (demo)`)}>Change</button>}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
        <button className="btn" style={{ width: '100%', padding: 14, borderStyle: 'dashed' }} onClick={() => toast('Add Block (demo)')}>+ Add Block</button>
      </div>
    </>
  );
}

// ── Shell (sidebar + topbar + content) ───────────────────────────────────────
function DemoShell({ persona, onExit, toast }) {
  // Three shell personas:
  //  - 'mal'          → admin-only layout (mirrors the real admin shell)
  //  - 'lead'         → team-lead-only layout
  //  - 'mal-combined' → both in one view (admin tabs + team-lead tabs), settings by the name
  const combined = persona.id === 'mal-combined';
  const flat = persona.id !== 'lead'; // admin + combined use the flat admin sidebar
  const NAVS = {
    'mal': [
      { id: 'overview', label: 'Overview', icon: 'overview' },
      { id: 'people', label: 'People', icon: 'people' },
      { id: 'rotations', label: 'Rotations', icon: 'rotations' },
      { id: 'daily', label: 'Daily Details', icon: 'schedule' },
      { id: 'meals', label: 'Meal Totals', icon: 'meals' },
      { id: 'detail-sheets', label: 'Detail Sheets', icon: 'manual' },
      { id: 'access', label: 'Team Lead Access', icon: 'access' },
      { id: 'manual', label: 'SL Manual', icon: 'manual' },
      { id: 'settings', label: 'Settings', icon: 'settings' },
    ],
    'lead': [
      { id: 'people', label: 'People', icon: 'people' },
      { id: 'Elementary', label: 'Elementary Roster', icon: 'roster' },
      { id: '5-6', label: '5-6 Roster', icon: 'roster' },
      { id: '3-4', label: '3-4 Roster', icon: 'roster' },
      { id: 'schedule', label: 'Team Schedule', icon: 'schedule' },
      { id: 'docs', label: 'Team Documents', icon: 'manual' },
      { id: 'contributing', label: 'Contributing Teams', icon: 'teams' },
    ],
    'mal-combined': [
      { id: 'overview', label: 'Overview', icon: 'overview' },
      { id: 'people', label: 'People', icon: 'people' },
      { id: 'Elementary', label: 'Elementary Roster', icon: 'roster' },
      { id: '5-6', label: '5-6 Roster', icon: 'roster' },
      { id: '3-4', label: '3-4 Roster', icon: 'roster' },
      { id: 'contributing', label: 'Contributing Teams', icon: 'teams' },
      { id: 'schedule', label: 'Team Schedule', icon: 'schedule' },
      { divider: true },
      { id: 'rotations', label: 'Rotations', icon: 'rotations' },
      { id: 'daily', label: 'Daily Details', icon: 'schedule' },
      { id: 'detail-sheets', label: 'Detail Sheets', icon: 'manual' },
      { id: 'docs', label: 'Team Documents', icon: 'manual' },
    ],
  };
  const nav = NAVS[persona.id] || NAVS.mal;
  const who = persona.id === 'lead' ? { name: 'Kids Lead (demo)', role: 'Team Lead' }
    : combined ? { name: 'Mal (demo)', role: 'Kids Admin + Lead' }
      : { name: 'Mal (demo)', role: 'Kids Admin' };
  const [view, setView] = useState(nav[0].id);
  const titleFor = id => id === 'settings' ? 'Settings' : (nav.find(n => n.id === id) || {}).label || 'Kids';

  function content() {
    if (view === 'overview') return <MalOverview setView={setView} />;
    if (view === 'people') return <KidsPeoplePage toast={toast} />;
    if (PROGRAMS.includes(view)) return <KidsRosterPage program={view} toast={toast} />;
    if (view === 'rotations') return <RotationsPage />;
    if (view === 'daily') return <DailyDetailsPage />;
    if (view === 'meals') return <MealsPage />;
    if (view === 'detail-sheets') return <DetailSheetsPage toast={toast} />;
    if (view === 'access') return <KidsAccessPage toast={toast} />;
    if (view === 'manual') return <KidsManualPage toast={toast} />;
    if (view === 'contributing') return <ContributingPage />;
    if (view === 'settings') return <KidsSettingsPage toast={toast} />;
    if (view === 'schedule') return (
      <>
        <div style={{ padding: '24px 40px 20px', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
          <div className="page-title-row" style={{ marginBottom: 0 }}>
            <div><div className="page-title">Conference Schedule</div><div className="page-sub">Kids · 2026</div></div>
            <button className="btn btn-dark" onClick={() => toast('Add Team Item (demo)')}>+ Add Team Item</button>
          </div>
        </div>
        <div className="page-content" style={{ paddingTop: 24 }}>
          <div className="section-header">Friday</div>
          <div className="panel" style={{ marginBottom: 24 }}>
            {[['9:00 AM', 'Elementary — Session 1', 'Kids Wing A'], ['4:00 PM', 'Elementary — Session 2', 'Kids Wing A'], ['6:30 PM', 'Dinner', 'Volunteer Lounge']].map((r, i) => (
              <div className="schedule-item" key={i}><div className="schedule-time">{r[0]}</div><div className="schedule-detail"><div className="schedule-name">{r[1]}</div><div className="schedule-loc">{r[2]}</div></div></div>
            ))}
          </div>
          <div className="section-header">Saturday</div>
          <div className="panel">
            {[['9:00 AM', 'Elementary — Session 1', 'Kids Wing A'], ['1:00 PM', 'Lunch', 'Volunteer Lounge'], ['4:00 PM', 'Elementary — Session 2', 'Kids Wing A']].map((r, i) => (
              <div className="schedule-item" key={i}><div className="schedule-time">{r[0]}</div><div className="schedule-detail"><div className="schedule-name">{r[1]}</div><div className="schedule-loc">{r[2]}</div></div></div>
            ))}
          </div>
        </div>
      </>
    );
    if (view === 'docs') return (
      <>
        <div style={{ padding: '24px 40px 20px', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
          <div className="page-title-row" style={{ marginBottom: 0 }}>
            <div><div className="page-title">Team Documents</div><div className="page-sub">Kids · visible to your team members</div></div>
            <button className="btn btn-dark" onClick={() => toast('Add Document (demo)')}>+ Add Document</button>
          </div>
        </div>
        <div className="page-content" style={{ paddingTop: 24, maxWidth: 560 }}>
          {[['Kids Team Manual', 'Roles, expectations, check-in flow'], ['Rotations — Detail Sheet', 'Rotation map for all programs'], ['Allergy & Custody Policy', 'Required reading for all leaders']].map((d, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1 }}><div style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 800, color: 'var(--black)' }}>{d[0]}</div><div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>{d[1]}</div></div>
              <button className="btn btn-sm" onClick={() => toast('Open document (demo)')}>Open</button>
            </div>
          ))}
        </div>
      </>
    );
    return null;
  }

  return (
    <div className="theme-kids">
      <nav className="sidebar">
        <div className="sidebar-logo">The Belonging Co<span>Kids Conference 2026</span></div>
        <div className="sidebar-nav">
          {flat ? (
            nav.map((item, i) => item.divider
              ? <div key={`d${i}`} style={{ borderTop: '1px solid rgba(255,255,255,0.18)', margin: '10px 24px 6px' }} />
              : (
                <button key={item.id} className={`nav-item${view === item.id ? ' active' : ''}`} onClick={() => setView(item.id)}>
                  {ICONS[item.icon]}{item.label}
                </button>
              ))
          ) : (
            <>
              <div className="nav-sub-label">Kids</div>
              {nav.map(item => (
                <button key={item.id} className={`nav-sub-item${view === item.id ? ' active' : ''}`} onClick={() => setView(item.id)}>
                  {ICONS[item.icon]}{item.label}
                </button>
              ))}
            </>
          )}
        </div>
        <div className="sidebar-user">
          {combined && (
            <button
              onClick={() => setView('settings')}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 600, padding: '8px 0', marginBottom: 10, background: 'none', color: view === 'settings' ? '#fff' : 'rgba(255,255,255,0.7)' }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}><circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5" /><path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M2.929 2.929l1.06 1.06M12.01 12.01l1.06 1.06M2.929 13.07l1.06-1.06M12.01 3.99l1.06-1.06" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
              Settings
            </button>
          )}
          <div className="sidebar-user-name">{who.name}</div>
          <div className="sidebar-user-role">{who.role}</div>
        </div>
      </nav>
      <div className="main">
        <div className="topbar">
          <div className="topbar-title">{titleFor(view)}</div>
          <div className="topbar-right">
            <span className="pill" style={{ background: ACCENT, color: '#202020' }}>DEMO</span>
            <span className="year-badge">2026</span>
            <button className="btn btn-sm" onClick={onExit}>Switch persona</button>
          </div>
        </div>
        {content()}
      </div>
    </div>
  );
}

// ── SL schedule personas ─────────────────────────────────────────────────────
function ScheduleList({ items, multiDept }) {
  const byDay = {};
  items.forEach(i => { (byDay[i.day] = byDay[i.day] || []).push(i); });
  const h = { fontFamily: 'var(--font-h)', fontWeight: 900, color: 'var(--black)' };
  const pill = (bg, c) => ({ fontFamily: 'var(--font-h)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', borderRadius: 100, padding: '3px 9px', background: bg, color: c });
  return Object.keys(byDay).map(day => (
    <div key={day} style={{ marginBottom: 28 }}>
      <div style={{ ...h, fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', paddingBottom: 10, borderBottom: '2px solid var(--black)' }}>{day}</div>
      {byDay[day].map((it, i) => (
        <div key={i} style={{ display: 'flex', gap: 16, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
          <div style={{ ...h, fontSize: 12, fontWeight: 700, color: 'var(--muted)', width: 76 }}>{fmt(it.start)}</div>
          <div style={{ flex: 1 }}>
            <div style={{ ...h, fontSize: 15, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {it.title}
              {multiDept && it.dept && <span style={pill(it.dept === 'Kids' ? TEAL : '#202020', '#fff')}>{it.dept}</span>}
              {it.meal && <span style={pill('#F2DADA', '#8B3A3A')}>Meal</span>}
            </div>
            {it.pos && <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{it.pos}</div>}
          </div>
        </div>
      ))}
    </div>
  ));
}

function AvailabilityForm() {
  const [count, setCount] = useState(2);
  const [sel, setSel] = useState(new Set());
  const [done, setDone] = useState(false);
  const btn = (bg, c) => ({ fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 13, padding: '10px 16px', border: 'none', borderRadius: 6, cursor: 'pointer', background: bg, color: c });
  if (done) return <div style={{ marginTop: 16, fontFamily: 'var(--font-b)', fontSize: 14, color: 'var(--mid)' }}>✓ Thanks! You can serve up to <strong>{count}</strong> and you're free for <strong>{sel.size}</strong>. <button style={{ ...btn('#fff', '#202020'), border: '2px solid var(--border)', marginLeft: 8 }} onClick={() => setDone(false)}>Edit</button></div>;
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>How many sessions can you serve?</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {[1, 2, 3, 4].map(n => <button key={n} onClick={() => setCount(n)} style={{ fontFamily: 'var(--font-h)', fontWeight: 800, minWidth: 42, padding: '8px 12px', borderRadius: 6, cursor: 'pointer', border: `2px solid ${count === n ? TEAL : 'var(--border)'}`, background: count === n ? TEAL : '#fff', color: count === n ? '#fff' : 'var(--mid)' }}>{n}</button>)}
      </div>
      <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>Which are you free for?</div>
      {DEMO_SESSIONS.map(s => {
        const on = sel.has(s.id);
        return (
          <label key={s.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 6, cursor: 'pointer', background: on ? 'rgba(106,162,146,0.1)' : '#fff' }}>
            <input type="checkbox" checked={on} onChange={() => setSel(p => { const n = new Set(p); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n; })} style={{ width: 18, height: 18, accentColor: TEAL }} />
            <span style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 700 }}>{s.title}</span>
            <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>{s.day} · {fmt(s.start)} · {s.period}</span>
          </label>
        );
      })}
      <button disabled={!sel.size} onClick={() => setDone(true)} style={{ ...btn(sel.size ? ACCENT : 'var(--border)', sel.size ? '#202020' : 'var(--muted)'), marginTop: 8 }}>Submit availability</button>
    </div>
  );
}

function SLFrame({ persona, onExit }) {
  const kids = persona.id === 'sl-kids';
  const cfg = kids
    ? { name: 'Jordan Lee', sub: 'Kids · Kids Pass · Small Group Leader (Red)', group: 'Red', schedule: KIDS_ONLY_SCHEDULE, multiDept: false }
    : { name: 'Sam Rivera', sub: 'Production · Kids · AA Pass · one badge, one radio', group: 'Blue', schedule: BOTH_SCHEDULE, multiDept: true };
  const tabs = [
    { key: 'schedule', label: 'My Schedule' },
    { key: 'rotations', label: 'My Rotations' },
    { key: 'availability', label: 'My Availability' },
    { key: 'manual', label: 'SL Manual' },
  ];
  const [view, setView] = useState('schedule');
  const active = tabs.find(t => t.key === view);
  const SIDEBAR_W = 220;

  const path = ROT_INIT.sessions.map(s => ({
    session: s,
    activity: ROT_INIT.activities.find(a => ROT_INIT.grid[s] && ROT_INIT.grid[s][a] === cfg.group) || '—',
  }));

  function content() {
    if (view === 'schedule') return (
      <div style={{ maxWidth: 640 }}>
        {cfg.multiDept && (
          <div style={{ background: 'rgba(106,162,146,0.08)', borderLeft: `4px solid ${TEAL}`, borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: TEAL, marginBottom: 4 }}>Also serving (read-only)</div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--mid)' }}>Production · Fri 7:00 AM and Sat 9:00 PM — so your Kids lead schedules around it.</div>
          </div>
        )}
        <ScheduleList items={cfg.schedule} multiDept={cfg.multiDept} />
        {kids && (
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderTop: `4px solid ${TEAL}`, borderRadius: 8, padding: 20, marginTop: 8 }}>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 15, fontWeight: 900, marginBottom: 6 }}>Kids meal rule</div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--mid)', lineHeight: 1.6 }}>Friday morning + evening → all-day → Dinner earned. Saturday morning only → no meal.</div>
          </div>
        )}
      </div>
    );
    if (view === 'rotations') return (
      <div style={{ maxWidth: 420 }}>
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, color: 'var(--mid)', marginBottom: 16, lineHeight: 1.6 }}>
          You're with the <strong style={{ color: 'var(--black)' }}>{cfg.group}</strong> group. Here's where your group goes each session:
        </div>
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: 16 }}>
          <span className="pill" style={{ background: TEAL, color: '#fff', display: 'inline-block', marginBottom: 12 }}>{cfg.group} group</span>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', textAlign: 'left', padding: '0 10px 8px 0', borderBottom: '1px solid var(--border)' }}>Session</th>
              <th style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', textAlign: 'left', padding: '0 0 8px', borderBottom: '1px solid var(--border)' }}>Activity</th>
            </tr></thead>
            <tbody>{path.map(r => (
              <tr key={r.session}>
                <td style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)', padding: '8px 10px 8px 0', borderBottom: '1px solid var(--border)' }}>{r.session}</td>
                <td style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--mid)', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>{r.activity}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    );
    if (view === 'availability') return (
      <div style={{ maxWidth: 560 }}>
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, color: 'var(--mid)', marginBottom: 4, lineHeight: 1.6 }}>
          Tell your Kids lead how many sessions you can serve, then check the ones you're free for.
        </div>
        <AvailabilityForm />
      </div>
    );
    return (
      <div style={{ maxWidth: 640 }}>
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ background: TEAL, color: '#fff', padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700 }}>Kids SL Manual 2026</span>
            <span style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, opacity: 0.85 }}>Open in new tab ↗</span>
          </div>
          <div style={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off)', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>
            (Manual PDF would embed here)
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--off)', display: 'flex' }}>
      {/* Teal sidebar */}
      <div style={{ width: SIDEBAR_W, flexShrink: 0, background: TEAL, position: 'fixed', top: 0, left: 0, bottom: 0, display: 'flex', flexDirection: 'column', zIndex: 200 }}>
        <div style={{ padding: '22px 24px 20px', borderBottom: '1px solid rgba(255,255,255,0.18)' }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 900, color: '#fff', letterSpacing: '0.02em' }}>THE BELONGING CO</div>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.15em', textTransform: 'uppercase', marginTop: 3 }}>KIDS CONFERENCE 2026</div>
        </div>
        <div style={{ flex: 1, paddingTop: 8 }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', padding: '12px 24px 6px' }}>MY KIDS CONFERENCE</div>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setView(t.key)} style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 600, color: view === t.key ? '#fff' : 'rgba(255,255,255,0.7)', padding: '9px 24px', cursor: 'pointer', border: 'none', background: view === t.key ? 'rgba(255,255,255,0.2)' : 'none', width: '100%', textAlign: 'left' }}>{t.label}</button>
          ))}
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.18)', padding: '16px 24px' }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{cfg.name}</div>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>Servant Leader</div>
        </div>
      </div>

      {/* Main */}
      <div style={{ marginLeft: SIDEBAR_W, flex: 1, minHeight: '100vh' }}>
        <div style={{ height: 60, background: '#fff', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px', position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 15, fontWeight: 800, color: 'var(--black)' }}>{active?.label}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="pill" style={{ background: ACCENT, color: '#202020' }}>DEMO</span>
            <span className="year-badge">2026</span>
            <button className="btn btn-sm" onClick={onExit}>Switch persona</button>
          </div>
        </div>
        <div style={{ padding: '32px 40px 80px' }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 26, fontWeight: 900, color: 'var(--black)', marginBottom: 2 }}>{cfg.name}</div>
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, color: 'var(--muted)', marginBottom: 28 }}>{cfg.sub}</div>
          {content()}
        </div>
      </div>
    </div>
  );
}

// ── Root ─────────────────────────────────────────────────────────────────────
export default function KidsDemo() {
  const [persona, setPersona] = useState(null);
  const [toastMsg, setToastMsg] = useState('');
  const toast = m => { setToastMsg(m); setTimeout(() => setToastMsg(''), 2200); };
  const exit = () => setPersona(null);

  if (!persona) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--off)' }}>
        <div style={{ background: TEAL, padding: '28px 24px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-h)', fontWeight: 900, color: '#fff', fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' }}>The Belonging Co</div>
          <div style={{ fontFamily: 'var(--font-h)', fontWeight: 900, color: '#fff', fontSize: 26, marginTop: 4 }}>Kids Conference — Demo</div>
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, color: 'rgba(255,255,255,0.85)', marginTop: 4 }}>Pick a persona. Fake sample data, resets on reload.</div>
        </div>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {PERSONAS.map(p => (
            <button key={p.id} onClick={() => setPersona(p)} style={{ textAlign: 'left', background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: 20, cursor: 'pointer' }}>
              <div style={{ fontFamily: 'var(--font-h)', fontWeight: 900, fontSize: 16, marginBottom: 6 }}>{p.label}</div>
              <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>{p.desc}</div>
              <div className="pill" style={{ background: ACCENT, color: '#202020', display: 'inline-block', marginTop: 12 }}>Open demo →</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      {persona.kind === 'shell'
        ? <DemoShell persona={persona} onExit={exit} toast={toast} />
        : <SLFrame persona={persona} onExit={exit} />}
      {toastMsg && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#202020', color: '#fff', fontFamily: 'var(--font-h)', fontWeight: 700, fontSize: 13, padding: '12px 24px', borderRadius: 100, zIndex: 999 }}>{toastMsg}</div>}
    </>
  );
}
